// BranchMenuEditor — per-branch overrides on top of the brand's master catalog.
//
// What this lets the admin do:
//   • Pick "Inherit" or "Curated" mode (two strategies for how items appear)
//   • Override price / name / hide / unavailable per item
//   • Override price / hide per variant
//   • Hide whole categories
//
// Inherit mode = ALL brand items show by default; overrides modify or hide.
// Curated mode = ONLY items with override rows (and is_hidden=false) appear.

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import BrandNav from '../../components/common/BrandNav';

export default function BranchMenuEditor() {
  const { brandId, branchId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [brand, setBrand]             = useState(null);
  const [branch, setBranch]           = useState(null);
  const [strategy, setStrategy]       = useState('inherit'); // 'inherit' | 'curated'
  const [categories, setCategories]   = useState([]);
  const [itemsByCat, setItemsByCat]   = useState({});  // catId → items[]
  const [variants, setVariants]       = useState({});  // itemId → variants[]
  const [itemOv, setItemOv]           = useState({});  // itemId → override row
  const [variantOv, setVariantOv]     = useState({});  // variantId → override row
  const [catOv, setCatOv]             = useState({});  // catId → override row
  const [collapsed, setCollapsed]     = useState({});  // catId → bool (UI only)
  const [allBranches, setAllBranches] = useState([]);  // every branch of the brand (for switcher + copy modal)
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyTargets, setCopyTargets] = useState({});   // branchId → bool
  const [copyReplace, setCopyReplace] = useState(true); // true = wipe target first
  const [copying, setCopying]         = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [bRes, brRes, catsRes, ovRes] = await Promise.all([
        api.get(`/brands/${brandId}`),
        api.get(`/branches/${branchId}`),
        api.get(`/categories?brand_id=${brandId}`),
        api.get(`/branches/${branchId}/overrides`),
      ]);
      setBrand(bRes.data);
      setBranch(brRes.data);
      setCategories(catsRes.data);
      setStrategy(ovRes.data.branch?.menu_strategy || 'inherit');

      // Index overrides by their target id
      const itemMap = {};
      for (const o of (ovRes.data.item_overrides || []))     itemMap[o.item_id] = o;
      const varMap = {};
      for (const o of (ovRes.data.variant_overrides || []))  varMap[o.variant_id] = o;
      const catMap = {};
      for (const o of (ovRes.data.category_overrides || [])) catMap[o.category_id] = o;
      setItemOv(itemMap);
      setVariantOv(varMap);
      setCatOv(catMap);

      // Fetch items + variants per category
      const newItems = {};
      const newVariants = {};
      await Promise.all(catsRes.data.map(async cat => {
        const iRes = await api.get(`/items?category_id=${cat.id}`);
        newItems[cat.id] = iRes.data;
        await Promise.all(iRes.data.map(async item => {
          try {
            const vRes = await api.get(`/items/${item.id}/variants`);
            newVariants[item.id] = vRes.data;
          } catch { newVariants[item.id] = []; }
        }));
      }));
      setItemsByCat(newItems);
      setVariants(newVariants);

      // All branches of this brand — used by the header switcher AND
      // (filtered to exclude the current one) by the "Copy to branches" picker.
      try {
        const sibs = await api.get(`/branches?brand_id=${brandId}`);
        setAllBranches(sibs.data || []);
      } catch { setAllBranches([]); }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load branch menu');
    } finally { setLoading(false); }
  }, [brandId, branchId]);

  useEffect(() => { load(); }, [load]);

  // ── Local mutators (don't hit network until Save) ──
  const setItemField = (itemId, field, value) => {
    setItemOv(prev => {
      const next = { ...prev };
      const cur  = next[itemId] || { item_id: itemId };
      next[itemId] = { ...cur, [field]: value };
      return next;
    });
  };
  const setVariantField = (variantId, field, value) => {
    setVariantOv(prev => {
      const next = { ...prev };
      const cur  = next[variantId] || { variant_id: variantId };
      next[variantId] = { ...cur, [field]: value };
      return next;
    });
  };
  const setCatField = (catId, field, value) => {
    setCatOv(prev => {
      const next = { ...prev };
      const cur  = next[catId] || { category_id: catId };
      next[catId] = { ...cur, [field]: value };
      return next;
    });
  };

  const resetItem = (itemId) => {
    setItemOv(prev => { const n = { ...prev }; delete n[itemId]; return n; });
  };
  const resetVariant = (variantId) => {
    setVariantOv(prev => { const n = { ...prev }; delete n[variantId]; return n; });
  };
  const resetCat = (catId) => {
    setCatOv(prev => { const n = { ...prev }; delete n[catId]; return n; });
  };

  // ── Save ──
  const handleSave = async () => {
    setSaving(true);
    try {
      // Persist strategy first (independent of overrides table)
      await api.put(`/branches/${branchId}/menu-strategy`, { strategy });

      // Build payload — only include rows that actually have something set,
      // OR rows that previously existed but are now empty (so backend deletes them).
      // Easy approach: send everything, backend's "isDefault" check deletes empty rows.
      const item_overrides = Object.values(itemOv);
      const variant_overrides = Object.values(variantOv);
      const category_overrides = Object.values(catOv);
      // ALSO include "delete" markers for items/variants/cats that had a row
      // before but no longer (resetItem etc. removed them from state).
      // Simplest: backend treats absent rows as "no change". We just send what's present.
      // For deletions to work we'd need to track originals. Easier: re-fetch original
      // overrides and add empty rows for any IDs no longer in state.
      const orig = await api.get(`/branches/${branchId}/overrides`);
      for (const o of (orig.data.item_overrides || []))     if (!itemOv[o.item_id])         item_overrides.push({ item_id: o.item_id });
      for (const o of (orig.data.variant_overrides || []))  if (!variantOv[o.variant_id])   variant_overrides.push({ variant_id: o.variant_id });
      for (const o of (orig.data.category_overrides || [])) if (!catOv[o.category_id])      category_overrides.push({ category_id: o.category_id });

      await api.put(`/branches/${branchId}/overrides`, {
        item_overrides, variant_overrides, category_overrides,
      });
      toast.success('Branch menu saved');
      load();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  // Copy this branch's overrides + strategy to other branches of the same brand.
  const runCopy = async () => {
    const targets = Object.entries(copyTargets).filter(([, v]) => v).map(([k]) => parseInt(k, 10));
    if (targets.length === 0) { toast.error('Pick at least one target branch'); return; }
    setCopying(true);
    try {
      const res = await api.post(`/branches/${branchId}/copy-overrides-to`, {
        target_branch_ids: targets,
        replace: copyReplace,
      });
      toast.success(`Copied to ${res.data.targets_count} branch${res.data.targets_count === 1 ? '' : 'es'} · ${res.data.rows_copied} rows`);
      setShowCopyModal(false);
      setCopyTargets({});
    } catch (e) {
      toast.error(e.response?.data?.error || 'Copy failed');
    } finally { setCopying(false); }
  };

  // Sibling branches = all branches of the brand minus the current one;
  // used by the copy modal so the user doesn't see "copy to self".
  const siblingBranches = allBranches.filter(b => String(b.id) !== String(branchId));

  if (loading) return <div className="spinner" style={{ marginTop: 60 }} />;

  // ── Helper render for an item row ──
  const renderItem = (cat, item) => {
    const ov = itemOv[item.id] || {};
    const hasOverride = !!itemOv[item.id]
      && (ov.override_price != null || ov.override_name || ov.override_name_ar
          || ov.override_description || ov.override_description_ar
          || ov.override_image_path || ov.is_hidden || ov.is_unavailable
          || ov.override_sort_order != null || ov.notes);
    const masterPrice = item.price ?? '';
    const effPrice = ov.override_price != null ? ov.override_price : masterPrice;
    const effName  = ov.override_name || item.name;
    const itemVariants = variants[item.id] || [];

    return (
      <div key={item.id}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '10px 14px', borderBottom: '1px solid #F2F2F2',
          background: ov.is_hidden ? '#FFF5F5' : (hasOverride ? '#FEFCE8' : '#fff'),
          opacity: ov.is_hidden ? 0.65 : 1,
        }}>
        {/* Photo */}
        {(item.image_thumb || item.image_path) ? (
          <img src={item.image_thumb || item.image_path} alt=""
            style={{ width: 38, height: 38, borderRadius: 6, objectFit: 'contain', background: '#f5f5f5', flexShrink: 0 }} />
        ) : (
          <div style={{ width: 38, height: 38, borderRadius: 6, background: '#F5F5F5', flexShrink: 0,
                         display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
            {item.icon || '🍽'}
          </div>
        )}

        {/* Name / desc */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {effName}
            {hasOverride && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: '#A16207', background: '#FEF3C7', borderRadius: 4, padding: '1px 5px', verticalAlign: 'middle' }}>OVERRIDE</span>}
          </div>
          {item.name_ar && (
            <div style={{ fontSize: 12, color: '#999', fontFamily: 'Cairo', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {ov.override_name_ar || item.name_ar}
            </div>
          )}
          {/* Variants */}
          {itemVariants.length > 0 && (
            <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {itemVariants.map(v => {
                const vov = variantOv[v.id] || {};
                const masterVPrice = v.price ?? '';
                return (
                  <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                    <span style={{ color: '#666', minWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      → {v.name}{v.name_ar ? ` (${v.name_ar})` : ''}
                    </span>
                    <input
                      type="number" step="0.01"
                      placeholder={String(masterVPrice)}
                      value={vov.override_price ?? ''}
                      onChange={e => {
                        const val = e.target.value === '' ? null : parseFloat(e.target.value);
                        setVariantField(v.id, 'override_price', val);
                      }}
                      style={{ width: 80, padding: '3px 6px', fontSize: 12, border: vov.override_price != null ? '1.5px solid #FBBF24' : '1px solid #E0E0E0', borderRadius: 5 }}
                    />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 11, color: '#888' }}>
                      <input type="checkbox" checked={!!vov.is_hidden}
                        onChange={e => setVariantField(v.id, 'is_hidden', e.target.checked)} />
                      Hide
                    </label>
                    {(vov.override_price != null || vov.is_hidden) &&
                      <button onClick={() => resetVariant(v.id)} title="Reset variant"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: 11 }}>↺</button>
                    }
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Price input (only when no variants) */}
        {itemVariants.length === 0 && (
          <input
            type="number" step="0.01"
            placeholder={String(masterPrice)}
            value={ov.override_price ?? ''}
            onChange={e => {
              const val = e.target.value === '' ? null : parseFloat(e.target.value);
              setItemField(item.id, 'override_price', val);
            }}
            style={{
              width: 90, padding: '5px 8px', fontSize: 13,
              border: ov.override_price != null ? '1.5px solid #FBBF24' : '1px solid #E0E0E0',
              borderRadius: 6, textAlign: 'right',
            }}
          />
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center', flexShrink: 0 }}>
          <label title="Hide on this branch" style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 10, color: '#666' }}>
            <input type="checkbox" checked={!!ov.is_hidden}
              onChange={e => setItemField(item.id, 'is_hidden', e.target.checked)} />
            Hide
          </label>
          <label title="Mark out-of-stock today (visible but greyed)" style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 10, color: '#666' }}>
            <input type="checkbox" checked={!!ov.is_unavailable}
              onChange={e => setItemField(item.id, 'is_unavailable', e.target.checked)} />
            Out
          </label>
          {hasOverride && (
            <button onClick={() => resetItem(item.id)} title="Clear all overrides for this item"
              style={{ background: 'none', border: '1px solid #E0E0E0', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', color: '#666', fontSize: 10 }}>↺ Reset</button>
          )}
        </div>
      </div>
    );
  };

  const overrideCount = Object.keys(itemOv).length + Object.keys(variantOv).length + Object.keys(catOv).length;

  return (
    <div className="page" style={{ paddingBottom: 80 }}>
      <BrandNav brandId={brandId} subPath="menu" backLabel="Branches"
                backPath={`/brands/${brandId}/branches`} backKey="branches" />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 className="page-title" style={{ margin: 0, whiteSpace: 'nowrap' }}>
              Branch Menu —
            </h1>
            {/* Branch switcher — replaces the static name so the user can hop
                between branches of the same brand without going back to the list. */}
            {allBranches.length > 0 ? (
              <select
                value={branchId}
                onChange={e => {
                  const newId = e.target.value;
                  if (String(newId) !== String(branchId)) {
                    navigate(`/brands/${brandId}/branches/${newId}/menu`);
                  }
                }}
                style={{
                  height: 38, padding: '0 12px', borderRadius: 10,
                  border: '1.5px solid #E0E0E0', background: '#FAFAFA',
                  fontSize: 16, fontWeight: 700, color: '#111',
                  cursor: 'pointer', maxWidth: '100%',
                  fontFamily: 'inherit',
                }}>
                {allBranches.map(b => (
                  <option key={b.id} value={b.id}>
                    📍 {b.name}{b.name_ar ? ` · ${b.name_ar}` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <span style={{ fontSize: 22, fontWeight: 700, color: '#111' }}>{branch?.name}</span>
            )}
          </div>
          <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
            {brand?.name} · {overrideCount} override{overrideCount !== 1 ? 's' : ''} on this branch
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {siblingBranches.length > 0 && (
            <button onClick={() => setShowCopyModal(true)} disabled={overrideCount === 0}
              title={overrideCount === 0 ? 'Add at least one override first' : `Copy this branch's overrides to other branches of ${brand?.name}`}
              style={{
                padding: '8px 14px', borderRadius: 8,
                border: '1.5px solid #FCD34D', background: '#FFFBEB', color: '#92400E',
                fontWeight: 700, fontSize: 13, cursor: overrideCount === 0 ? 'not-allowed' : 'pointer',
                opacity: overrideCount === 0 ? 0.5 : 1,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
              📋 Copy to branches…
            </button>
          )}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}
            style={{ minWidth: 120, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : '💾 Save'}
          </button>
        </div>
      </div>

      {/* Strategy toggle */}
      <div className="card" style={{ padding: 14, marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
          Menu strategy
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { val: 'inherit', label: 'Inherit',
              hint: 'Show ALL brand items. Use overrides to change price / hide individual items.' },
            { val: 'curated', label: 'Curated',
              hint: 'Show ONLY items you explicitly opt-in via the brand catalog. Everything else hidden.' },
          ].map(opt => (
            <button key={opt.val}
              onClick={() => setStrategy(opt.val)}
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 10,
                border: strategy === opt.val ? '2px solid #1565C0' : '1.5px solid #E0E0E0',
                background: strategy === opt.val ? '#E3F2FD' : '#fff',
                cursor: 'pointer', textAlign: 'left',
              }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: strategy === opt.val ? '#0D47A1' : '#333' }}>{opt.label}</div>
              <div style={{ fontSize: 11, color: '#666', marginTop: 3 }}>{opt.hint}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="card" style={{ padding: '8px 14px', marginBottom: 12, fontSize: 11, color: '#666', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <span><span style={{ background: '#FEFCE8', padding: '1px 6px', borderRadius: 3 }}>yellow row</span> = item has override</span>
        <span><span style={{ background: '#FFF5F5', padding: '1px 6px', borderRadius: 3 }}>pink row</span> = item hidden</span>
        <span>Empty price input = use master price (placeholder)</span>
      </div>

      {/* Categories + items */}
      {categories.length === 0 && (
        <div className="card" style={{ padding: 30, textAlign: 'center', color: '#999' }}>
          This brand has no categories yet. Create the master menu first via the brand menu editor.
        </div>
      )}

      {categories.map(cat => {
        const cov = catOv[cat.id] || {};
        const isCatHidden = !!cov.is_hidden;
        const isCollapsed = collapsed[cat.id];
        const items = itemsByCat[cat.id] || [];
        return (
          <div key={cat.id} className="card" style={{ marginBottom: 12, overflow: 'hidden', opacity: isCatHidden ? 0.55 : 1 }}>
            <div
              style={{
                padding: '12px 16px', borderBottom: '1px solid #E5E7EB',
                background: isCatHidden ? '#FFF5F5' : '#FAFBFC',
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
              <button onClick={() => setCollapsed(c => ({ ...c, [cat.id]: !c[cat.id] }))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#666' }}>
                {isCollapsed ? '▶' : '▼'}
              </button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#222' }}>{cat.name}</div>
                {cat.name_ar && <div style={{ fontSize: 12, color: '#888', fontFamily: 'Cairo' }}>{cat.name_ar}</div>}
              </div>
              <div style={{ fontSize: 11, color: '#999' }}>{items.length} items</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 11, color: '#666' }}>
                <input type="checkbox" checked={isCatHidden}
                  onChange={e => {
                    if (e.target.checked) setCatField(cat.id, 'is_hidden', true);
                    else resetCat(cat.id);
                  }} />
                Hide whole category
              </label>
            </div>
            {!isCollapsed && (
              <div>
                {items.length === 0 && (
                  <div style={{ padding: '14px 16px', color: '#bbb', fontSize: 12 }}>No items in this category</div>
                )}
                {items.map(item => renderItem(cat, item))}
              </div>
            )}
          </div>
        );
      })}

      {/* Floating Save (mobile-friendly) */}
      <div style={{ position: 'fixed', bottom: 16, left: 0, right: 0, padding: '0 16px', display: 'flex', justifyContent: 'center', pointerEvents: 'none', zIndex: 100 }}>
        <button onClick={handleSave} disabled={saving}
          style={{
            pointerEvents: 'auto',
            padding: '12px 26px', borderRadius: 999,
            background: '#1565C0', color: '#fff', border: 'none',
            fontWeight: 700, fontSize: 14, cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(21,101,192,0.4)', opacity: saving ? 0.6 : 1,
          }}>
          {saving ? 'Saving…' : '💾 Save Branch Menu'}
        </button>
      </div>

      {/* Copy-to-branches modal */}
      {showCopyModal && (
        <div className="modal-overlay" onClick={() => !copying && setShowCopyModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <span className="modal-title">📋 Copy overrides to other branches</span>
              <button className="modal-close" onClick={() => !copying && setShowCopyModal(false)}>✕</button>
            </div>
            <div style={{ padding: '4px 0 8px', fontSize: 13, color: '#555', lineHeight: 1.5 }}>
              Copies this branch's <strong>{overrideCount}</strong> override{overrideCount !== 1 ? 's' : ''}
              {' '}+ menu strategy ({strategy}) to the picked branches of <strong>{brand?.name}</strong>.
            </div>

            {/* Mode toggle */}
            <div style={{ marginTop: 4, marginBottom: 12, padding: 10, background: '#FAFAFA', borderRadius: 8, border: '1px solid #EEE' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', marginBottom: 6 }}>
                <input type="radio" checked={copyReplace} onChange={() => setCopyReplace(true)} style={{ marginTop: 2 }} />
                <span>
                  <strong style={{ fontSize: 13 }}>Replace</strong>
                  <div style={{ fontSize: 11, color: '#777' }}>Wipe target's existing overrides first, then copy. Targets become exact mirrors.</div>
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                <input type="radio" checked={!copyReplace} onChange={() => setCopyReplace(false)} style={{ marginTop: 2 }} />
                <span>
                  <strong style={{ fontSize: 13 }}>Merge</strong>
                  <div style={{ fontSize: 11, color: '#777' }}>Keep target's other overrides; only overwrite where this branch has a row.</div>
                </span>
              </label>
            </div>

            {/* Target picker */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Target branches ({Object.values(copyTargets).filter(Boolean).length} selected)
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setCopyTargets(Object.fromEntries(siblingBranches.map(b => [b.id, true])))}
                  style={{ background: 'none', border: '1px solid #DDD', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}>
                  All
                </button>
                <button onClick={() => setCopyTargets({})}
                  style={{ background: 'none', border: '1px solid #DDD', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}>
                  None
                </button>
              </div>
            </div>
            <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #EEE', borderRadius: 8 }}>
              {siblingBranches.map(b => (
                <label key={b.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 12px', borderBottom: '1px solid #F5F5F5',
                    cursor: 'pointer',
                    background: copyTargets[b.id] ? '#EFF6FF' : '#fff',
                  }}>
                  <input type="checkbox" checked={!!copyTargets[b.id]}
                    onChange={e => setCopyTargets(t => ({ ...t, [b.id]: e.target.checked }))} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      📍 {b.name}
                    </div>
                    {b.name_ar && <div style={{ fontSize: 12, color: '#888', fontFamily: 'Cairo' }}>{b.name_ar}</div>}
                  </div>
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button onClick={() => !copying && setShowCopyModal(false)} disabled={copying}
                style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1.5px solid #E0E0E0', background: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                Cancel
              </button>
              <button onClick={runCopy} disabled={copying || Object.values(copyTargets).filter(Boolean).length === 0}
                className="btn btn-primary"
                style={{ flex: 2, justifyContent: 'center', opacity: copying ? 0.6 : 1 }}>
                {copying ? 'Copying…' : `📋 Copy to ${Object.values(copyTargets).filter(Boolean).length || 0} branch(es)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
